import React, { useState, useMemo, useRef, useCallback } from 'react';
import { TeamInLeague } from '../../domain/entities';
import { View } from '../../App';
import { soundService } from '../../services/SoundService';
import { TeamBadge } from './TeamBadge';
import './LeaguePositionChart.css';

interface LeaguePositionChartProps {
    teams: TeamInLeague[];
    playerTeamName: string;
    pushView: (view: View) => void;
}

const parseColor = (color: string): [number, number, number] => {
    if (color.startsWith('#')) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
        return result 
            ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] 
            : [0, 255, 155];
    }
    const rgb = color.match(/\d+/g)?.map(Number);
    return rgb && rgb.length === 3 ? [rgb[0], rgb[1], rgb[2]] : [0, 255, 155];
};

const colorDistance = (rgb1: [number, number, number], rgb2: [number, number, number]): number => {
    const [r1, g1, b1] = rgb1;
    const [r2, g2, b2] = rgb2;
    return Math.sqrt(Math.pow(r1 - r2, 2) + Math.pow(g1 - g2, 2) + Math.pow(b1 - b2, 2));
};

const generateRandomColor = (): string => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};

export const LeaguePositionChart: React.FC<LeaguePositionChartProps> = ({ teams, playerTeamName, pushView }) => {
    const [hoveredTeam, setHoveredTeam] = useState<string | null>(null);
    const [tooltip, setTooltip] = useState<{ x: number, y: number, teamName: string, position: number, week: number } | null>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const chartData = useMemo(() => {
        const weeks = teams[0]?.positionHistory?.map(h => h.week) || [];
        if (weeks.length === 0) return null;
        const numTeams = teams.length;

        // Mobile-responsive sizing
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        const PADDING = isMobile
            ? { top: 15, right: 15, bottom: 40, left: 30 }
            : { top: 20, right: 20, bottom: 50, left: 40 };
        const WIDTH = isMobile ? Math.max(320, weeks.length * 20) : Math.max(600, weeks.length * 30);
        const HEIGHT = isMobile ? 300 : 400;

        const getX = (week: number) => {
            if (weeks.length <= 1) return PADDING.left;
            const weekIndex = weeks.indexOf(week);
            return PADDING.left + (weekIndex / (weeks.length - 1)) * (WIDTH - PADDING.left - PADDING.right);
        };
        const getY = (position: number) => PADDING.top + ((position - 1) / (numTeams - 1)) * (HEIGHT - PADDING.top - PADDING.bottom);

        const lines = teams.map(team => {
            const points = team.positionHistory.map(h => `${getX(h.week)},${getY(h.position)}`).join(' ');
            return { teamName: team.name, points };
        });

        return { PADDING, WIDTH, HEIGHT, weeks, numTeams, getX, getY, lines };
    }, [teams]);

    const teamStyles = useMemo(() => {
        const styles = new Map<string, { color: string; style?: string }>();
        const usedColors: [number, number, number][] = [];
        const sortedTeams = [...teams].sort((a,b) => a.points - b.points);

        for (const team of sortedTeams) {
            let primaryColor = team.colors.primary;
            let primaryRgb = parseColor(primaryColor);
            let clash = usedColors.some(usedRgb => colorDistance(primaryRgb, usedRgb) < 50);

            if (clash) {
                let secondaryColor = team.colors.secondary;
                let secondaryRgb = parseColor(secondaryColor);
                clash = usedColors.some(usedRgb => colorDistance(secondaryRgb, usedRgb) < 50);
                if (!clash) {
                    primaryColor = secondaryColor;
                    primaryRgb = secondaryRgb;
                } else {
                    let randomColor: string;
                    let randomRgb: [number, number, number];
                    let attempts = 0;
                    do {
                        randomColor = generateRandomColor();
                        randomRgb = parseColor(randomColor);
                        clash = usedColors.some(usedRgb => colorDistance(randomRgb, usedRgb) < 50);
                        attempts++;
                    } while (clash && attempts < 10);
                    primaryColor = randomColor;
                    primaryRgb = randomRgb;

                    if (clash) {
                         styles.set(team.name, { color: primaryColor, style: '4,4' });
                    } else {
                        styles.set(team.name, { color: primaryColor });
                    }
                    usedColors.push(primaryRgb);
                    continue;
                }
            }
            styles.set(team.name, { color: primaryColor });
            usedColors.push(primaryRgb);
        }
        return styles;
    }, [teams]);

    const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
        if (!svgRef.current || !chartData) return;
        const svgPoint = svgRef.current.createSVGPoint();
        svgPoint.x = e.clientX;
        svgPoint.y = e.clientY;
        const { x, y } = svgPoint.matrixTransform(svgRef.current.getScreenCTM()?.inverse());
        
        const weekIndex = Math.round((x - chartData.PADDING.left) / (chartData.WIDTH - chartData.PADDING.left - chartData.PADDING.right) * (chartData.weeks.length - 1));
        const week = chartData.weeks[weekIndex];

        let closestTeam: { name: string, position: number, dist: number } | null = null;
        for (const team of teams) {
            const historyPoint = team.positionHistory.find(h => h.week === week);
            if (historyPoint) {
                const teamY = chartData.getY(historyPoint.position);
                const dist = Math.abs(y - teamY);
                if (!closestTeam || dist < closestTeam.dist) {
                    closestTeam = { name: team.name, position: historyPoint.position, dist };
                }
            }
        }

        if (closestTeam && closestTeam.dist < 15) {
            setTooltip({
                x: e.clientX,
                y: e.clientY,
                teamName: closestTeam.name,
                position: closestTeam.position,
                week
            });
        } else {
            setTooltip(null);
        }
    }, [chartData, teams]);

    if (!chartData) {
        return <div className="panel-content"><p style={{textAlign: 'center'}}>Not enough data to display position history chart.</p></div>;
    }

    const { PADDING, WIDTH, HEIGHT, weeks, numTeams, getX, getY, lines } = chartData;

    return (
        <div className="league-position-chart-container" ref={containerRef} onMouseLeave={() => setTooltip(null)}>
            {tooltip && (
                <div className="chart-tooltip" style={{
                    left: Math.min(tooltip.x + 15, (containerRef.current?.clientWidth || 500) - 120),
                    top: tooltip.y + 15,
                    opacity: 1
                }}>
                    <strong>{tooltip.teamName}</strong><br/>
                    Position: {tooltip.position}<br/>
                    Week: {tooltip.week}
                </div>
            )}
            <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="league-position-chart-svg" onMouseMove={handleMouseMove}>
                {/* Y-Axis (Positions) */}
                {Array.from({ length: numTeams }, (_, i) => i + 1).map(pos => (
                    <g key={pos}>
                        <line x1={PADDING.left} y1={getY(pos)} x2={WIDTH - PADDING.right} y2={getY(pos)} className="position-chart-grid-line" />
                        <text x={PADDING.left - 8} y={getY(pos) + 4} textAnchor="end" className="position-chart-axis-text">{pos}</text>
                    </g>
                ))}
                
                {/* X-Axis (Weeks) */}
                {weeks.map(week => (
                    <g key={week}>
                        {week % 2 === 0 && <line x1={getX(week)} y1={PADDING.top} x2={getX(week)} y2={HEIGHT - PADDING.bottom} className="position-chart-week-marker" />}
                        <text x={getX(week)} y={HEIGHT - PADDING.bottom + 15} textAnchor="middle" className="position-chart-axis-text">{week}</text>
                    </g>
                ))}
                
                <line x1={PADDING.left} y1={PADDING.top} x2={PADDING.left} y2={HEIGHT - PADDING.bottom} className="position-chart-axis-line" />
                <line x1={PADDING.left} y1={HEIGHT - PADDING.bottom} x2={WIDTH - PADDING.right} y2={HEIGHT - PADDING.bottom} className="position-chart-axis-line" />

                {/* Team Lines */}
                {lines.map(({ teamName, points }) => (
                    <polyline
                        key={teamName}
                        points={points}
                        stroke={teamStyles.get(teamName)?.color || '#fff'}
                        strokeDasharray={teamStyles.get(teamName)?.style}
                        className={`position-chart-team-line ${hoveredTeam && hoveredTeam !== teamName ? 'dimmed' : ''}`}
                        onMouseEnter={() => setHoveredTeam(teamName)}
                        onMouseLeave={() => setHoveredTeam(null)}
                    />
                ))}
            </svg>
            <div className="chart-legend">
                {teams.map(team => (
                    <div
                        key={team.name}
                        className={`legend-item ${hoveredTeam && hoveredTeam !== team.name ? 'dimmed' : ''}`}
                        onMouseEnter={() => setHoveredTeam(team.name)}
                        onMouseLeave={() => setHoveredTeam(null)}
                        onClick={() => { soundService.playClick(); pushView({ type: 'teamDetail', title: `Team: ${team.name}`, teamName: team.name })}}
                    >
                        <div className="legend-color-box" style={{ backgroundColor: teamStyles.get(team.name)?.color }}></div>
                        <span className={team.name === playerTeamName ? 'player-team-name' : ''}>
                            {team.badge && <TeamBadge badge={team.badge} color={team.colors.primary} size="sm" />}
                            {team.name}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};
